import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  FiDownload,
  FiEdit,
  FiEye,
  FiFileText,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUploadCloud,
} from 'react-icons/fi';

import { useAuth } from '../contexts/AuthContext';
import { trainingService } from '../services/aiService';

// Konfiguration
const MAX_UPLOAD_FILES = 10; // Maximale Anzahl gleichzeitiger Uploads
const POLLING_INTERVAL = 5000; // Polling alle 5 Sekunden (reduziert von 2)
const MAX_POLLING_TIME = 300000; // Max 5 Minuten Polling

// Hilfsfunktion für korrekte Umlaut-Darstellung
function decodeFilename(filename) {
  try {
    let decoded = filename;
    const encodingFixes = {
      ä: /Ã¤/g,
      ö: /Ã¶/g,
      ü: /Ã¼/g,
      ß: /ÃŸ/g,
      Ä: /Ã„/g,
      Ö: /Ã–/g,
      Ü: /Ãœ/g,
    };
    Object.entries(encodingFixes).forEach(([correct, pattern]) => {
      decoded = decoded.replace(pattern, correct);
    });
    return decoded;
  } catch (error) {
    console.warn('Fehler beim Dekodieren des Dateinamens:', error);
    return filename;
  }
}

function Documents() {
  const { isDarkMode } = useAuth();

  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [editingDocument, setEditingDocument] = useState(null);
  const [editFilename, setEditFilename] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [chunkingStrategy, setChunkingStrategy] = useState('standard');
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState(null);

  // Dokumente laden
  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const data = await trainingService.getDocuments();
      setDocuments(data);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();

    // Globale Drag & Drop Event Handler
    const handleGlobalDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleGlobalDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  // Drag & Drop Event Handler
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragOverUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeaveUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDropUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  // Diese Drag & Drop Handler sind doppelt vorhanden - entfernt da bereits oben definiert

  // Dokument hochladen mit Fortschrittsanzeige
  const handleUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Upload-Limit prüfen
    if (acceptedFiles.length > MAX_UPLOAD_FILES) {
      toast.error(
        `❌ Maximale Anzahl von ${MAX_UPLOAD_FILES} Dokumenten überschritten!\n\nBitte wählen Sie maximal ${MAX_UPLOAD_FILES} Dateien aus.`,
        { duration: 6000 },
      );
      return;
    }

    setIsUploading(true);

    // Initialisiere Fortschritt für jede Datei
    const initialProgress = acceptedFiles.map((file, index) => ({
      id: index,
      filename: decodeFilename(file.name),
      status: 'uploading',
      progress: 0,
    }));
    setUploadProgress(initialProgress);

    try {
      const response = await trainingService.uploadDocuments(
        acceptedFiles,
        'company',
        chunkingStrategy,
      );

      // Markiere alle als hochgeladen
      const uploadedProgress = initialProgress.map((item) => ({
        ...item,
        status: 'processing',
        progress: 100,
      }));
      setUploadProgress(uploadedProgress);

      toast.success(`${response.documents.length} Dokument(e) erfolgreich hochgeladen`);

      // Überwache echten Vektorisierungsfortschritt
      if (response.documents && response.documents.length > 0) {
        monitorVectorization();
      }
    } catch (error) {
      console.error('Upload-Fehler:', error);
      toast.error(`Upload-Fehler: ${error.message}`);

      const failedProgress = initialProgress.map((item) => ({
        ...item,
        status: 'failed',
        progress: 0,
      }));
      setUploadProgress(failedProgress);

      setTimeout(() => setUploadProgress([]), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  // Vektorisierungsfortschritt überwachen (OPTIMIERT: Weniger API-Calls)
  const monitorVectorization = async () => {
    let pollCount = 0;
    const MAX_POLLS = Math.floor(MAX_POLLING_TIME / POLLING_INTERVAL);

    const checkProgress = async () => {
      try {
        const progressData = await trainingService.getAllUploadProgress();
        pollCount += 1;

        if (progressData.success && progressData.documents.length > 0) {
          // Aktualisiere Fortschritt basierend auf Backend-Status
          setUploadProgress((prev) => {
            return prev.map((item) => {
              const backendDoc = progressData.documents.find((d) => d.filename === item.filename);

              if (backendDoc) {
                return {
                  ...item,
                  status:
                    backendDoc.status === 'active'
                      ? 'completed'
                      : backendDoc.status === 'failed'
                        ? 'failed'
                        : 'processing',
                  progress: backendDoc.progress || item.progress,
                };
              }
              return item;
            });
          });

          // Prüfe ob alle abgeschlossen sind
          const allCompleted = progressData.documents.every(
            (doc) => doc.status === 'active' || doc.status === 'failed',
          );

          if (allCompleted) {
            // Zeige "✅ Fertig" für 4 Sekunden, bevor die Balken verschwinden
            setTimeout(() => {
              setUploadProgress([]);
              loadDocuments();
            }, 4000);
          } else if (pollCount < MAX_POLLS) {
            // Weiter überwachen mit konfiguriertem Intervall
            setTimeout(checkProgress, POLLING_INTERVAL);
          } else {
            // Max-Polling erreicht
            toast.error('Vektorisierung dauert zu lange. Bitte Seite neu laden.');
            setUploadProgress([]);
            loadDocuments();
          }
        }
      } catch (error) {
        console.error('Fehler beim Überwachen des Fortschritts:', error);
        // Fallback: Länger warten und dann neu laden
        setTimeout(() => {
          setUploadProgress([]);
          loadDocuments();
        }, 10000);
      }
    };

    // Starte Überwachung nach kurzer Verzögerung
    setTimeout(checkProgress, 2000);
  };

  // Dokument herunterladen
  const handleDownload = async (documentId, filename) => {
    try {
      await trainingService.downloadDocument(documentId);
      toast.success(`Download für "${filename}" gestartet`);
    } catch (error) {
      console.error('Download-Fehler:', error);
      toast.error(`Download fehlgeschlagen: ${error.message}`);
    }
  };

  // Dokument umbenennen
  const handleRename = async (documentId, newFilename) => {
    try {
      await trainingService.renameDocument(documentId, newFilename);
      toast.success('Dokument erfolgreich umbenannt');
      setEditingDocument(null);
      setEditFilename('');
      loadDocuments(); // Liste neu laden
    } catch (error) {
      console.error('Umbenennen-Fehler:', error);
      toast.error(`Umbenennen fehlgeschlagen: ${error.message}`);
    }
  };

  // Dokument löschen
  const handleDelete = async (documentId, filename) => {
    if (!window.confirm(`Möchten Sie das Dokument "${filename}" wirklich löschen?`)) {
      return;
    }

    try {
      await trainingService.deleteDocument(documentId);
      toast.success(`Dokument "${filename}" erfolgreich gelöscht`);
      loadDocuments(); // Liste neu laden
    } catch (error) {
      console.error('Löschen-Fehler:', error);
      toast.error(`Löschen fehlgeschlagen: ${error.message}`);
    }
  };

  // Sichtbarkeit ändern
  const handleVisibilityChange = async (documentId, newVisibility) => {
    try {
      await trainingService.updateDocumentVisibility(documentId, newVisibility);
      toast.success('Sichtbarkeit erfolgreich geändert');
      loadDocuments(); // Liste neu laden
    } catch (error) {
      console.error('Sichtbarkeits-Änderung-Fehler:', error);
      toast.error(`Sichtbarkeits-Änderung fehlgeschlagen: ${error.message}`);
    }
  };

  // Dokument für Vorschau laden (mit Authorization Header)
  const loadDocumentForView = async (document) => {
    try {
      setIsLoadingDocument(true);
      setDocumentError(null);
      setViewingDocument(document);

      const token = localStorage.getItem('msb-token');
      if (!token) {
        throw new Error('Keine Authentifizierung gefunden. Bitte melden Sie sich an.');
      }

      // Fetch das Dokument mit Authorization Header
      const response = await fetch(`/api/ki-workspace/chat/documents/${document.id}/view`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Konvertiere Response zu Blob
      const blob = await response.blob();

      // Erstelle Object URL für den Blob
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
    } catch (err) {
      console.error('Fehler beim Laden des Dokuments:', err);
      setDocumentError(err.message);
      toast.error(`Fehler beim Laden: ${err.message}`);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  // Modal schließen und Cleanup
  const closeDocumentView = () => {
    setViewingDocument(null);
    if (documentUrl) {
      URL.revokeObjectURL(documentUrl);
      setDocumentUrl(null);
    }
    setDocumentError(null);
  };

  // Dokumente filtern
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVisibility = visibilityFilter === 'all' || doc.visibility === visibilityFilter;
    return matchesSearch && matchesVisibility;
  });

  // Dateigröße formatieren
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  // Status-Badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        text: 'Aktiv',
      },
      processing: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        text: 'Verarbeitung',
      },
      failed: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        text: 'Fehler',
      },
      archived: {
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
        text: 'Archiviert',
      },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  return (
    <div
      className={`h-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} p-6`}
    >
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">📄 Dokumentenverwaltung</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Verwalten Sie Ihre Dokumente und deren Sichtbarkeit
        </p>
      </div>

      {/* Upload-Bereich */}
      <div className="mb-6">
        <div
          className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
            isDragOver
              ? isDarkMode
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-blue-500 bg-blue-50'
              : isDarkMode
                ? 'border-gray-700 bg-gray-800'
                : 'border-gray-300 bg-white'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOverUpload}
          onDragLeave={handleDragLeaveUpload}
          onDrop={handleDropUpload}
        >
          <FiUploadCloud
            className={`mx-auto text-4xl mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          />
          <h3 className="text-lg font-semibold mb-2">Dokumente hochladen</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {isDragOver
              ? '📁 Dateien hier ablegen...'
              : 'Ziehen Sie Dateien hierher oder klicken Sie zum Auswählen (Mehrfachauswahl möglich)'}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mb-4">
            ⚠️ Maximal {MAX_UPLOAD_FILES} Dokumente gleichzeitig hochladen
          </p>

          {/* Chunking-Strategie Auswahl */}
          <div className="mb-4 text-left max-w-2xl mx-auto">
            <label className="block text-sm font-medium mb-2">Chunking-Strategie wählen</label>

            {/* Standard */}
            <div
              onClick={() => setChunkingStrategy('standard')}
              className={`border rounded-lg p-3 mb-2 cursor-pointer transition-colors ${
                chunkingStrategy === 'standard'
                  ? isDarkMode
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-blue-500 bg-blue-50'
                  : isDarkMode
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="chunking_strategy"
                  value="standard"
                  checked={chunkingStrategy === 'standard'}
                  onChange={() => setChunkingStrategy('standard')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-semibold">Standard</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Für technische Dokumente mit zusammenhängenden Absätzen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Chunk-Größe: 250-550 Tokens | Overlap: 100 Tokens
                  </p>
                </div>
              </div>
            </div>

            {/* Detailreich */}
            <div
              onClick={() => setChunkingStrategy('detailreich')}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                chunkingStrategy === 'detailreich'
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                  : isDarkMode
                    ? 'border-gray-600 hover:border-gray-500'
                    : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="chunking_strategy"
                  value="detailreich"
                  checked={chunkingStrategy === 'detailreich'}
                  onChange={() => setChunkingStrategy('detailreich')}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-semibold">Detailreich</span>
                    <span className="ml-2 text-xs bg-amber-200 dark:bg-amber-700 px-2 py-0.5 rounded">
                      Für dichte Informationen
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Für Dokumente mit vielen Aufzählungen, Listen oder Tabellen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Chunk-Größe: 50-200 Tokens | Kein Overlap
                  </p>
                  <div className="mt-2 flex items-start bg-amber-100 dark:bg-amber-900/40 p-2 rounded">
                    <span className="text-amber-600 dark:text-amber-400 mr-2">⚠️</span>
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      <strong>Hinweis:</strong> Zusammenhänge zwischen Absätzen können verloren
                      gehen. Nur für sehr informationsdichte Dokumente empfohlen!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <input
            type="file"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
            disabled={isUploading}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
              isUploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isUploading ? (
              <>
                <FiRefreshCw className="animate-spin mr-2" />
                Upload läuft...
              </>
            ) : (
              <>
                <FiUploadCloud className="mr-2" />
                Dateien auswählen
              </>
            )}
          </label>
        </div>
      </div>

      {/* Upload-Fortschritt */}
      {uploadProgress.length > 0 && (
        <div
          className={`mb-6 rounded-lg p-4 ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          <h4 className="text-md font-semibold mb-3">📊 Upload & Vektorisierung</h4>
          <div className="space-y-3">
            {uploadProgress.map((item) => (
              <div key={item.id} className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{item.filename}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        item.status === 'uploading'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : item.status === 'processing'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : item.status === 'completed'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                    >
                      {item.status === 'uploading'
                        ? '📤 Hochladen'
                        : item.status === 'processing'
                          ? '🔄 Vektorisierung'
                          : item.status === 'completed'
                            ? '✅ Fertig'
                            : '❌ Fehler'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        item.status === 'completed'
                          ? 'bg-green-500'
                          : item.status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                      }`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter und Suche */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div
            className={`relative ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm`}
          >
            <FiSearch
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
            />
            <input
              type="text"
              placeholder="Dokumente durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isDarkMode
                  ? 'bg-gray-800 text-white placeholder-gray-400'
                  : 'bg-white text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}
          >
            <option value="all">Alle Sichtbarkeiten</option>
            <option value="private">Privat</option>
            <option value="company">Firma</option>
            <option value="global">Global</option>
          </select>
        </div>
      </div>

      {/* Dokumenten-Liste */}
      <div className={`rounded-lg shadow-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {isLoading ? (
          <div className="p-8 text-center">
            <FiRefreshCw className="animate-spin mx-auto text-4xl mb-4 text-blue-500" />
            <p className="text-gray-500 dark:text-gray-400">Dokumente werden geladen...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-8 text-center">
            <FiFileText className="mx-auto text-4xl mb-4 text-gray-400" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || visibilityFilter !== 'all'
                ? 'Keine Dokumente gefunden'
                : 'Noch keine Dokumente vorhanden'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dokument
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Größe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sichtbarkeit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y divide-gray-200 dark:divide-gray-700 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              >
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className={`hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiFileText className="text-blue-500 mr-3" />
                        <div>
                          {editingDocument === doc.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editFilename}
                                onChange={(e) => setEditFilename(e.target.value)}
                                className={`px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm ${
                                  isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
                                }`}
                                autoFocus
                              />
                              <button
                                onClick={() => handleRename(doc.id, editFilename)}
                                className="text-green-600 hover:text-green-800"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  setEditingDocument(null);
                                  setEditFilename('');
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {decodeFilename(doc.filename)}
                              </div>
                              {doc.chunking_strategy === 'detailreich' && (
                                <span className="ml-2 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 rounded border border-amber-300 dark:border-amber-700">
                                  Detailreich
                                </span>
                              )}
                            </div>
                          )}
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {doc.mimetype}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatFileSize(doc.filesize)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(doc.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={doc.visibility}
                        onChange={(e) => handleVisibilityChange(doc.id, e.target.value)}
                        className={`text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 ${
                          isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
                        }`}
                      >
                        <option value="private">Privat</option>
                        <option value="company">Firma</option>
                        <option value="global">Global</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(doc.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => loadDocumentForView(doc)}
                          className={`p-1 rounded ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                          title="Dokument im Browser anzeigen"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => handleDownload(doc.id, doc.filename)}
                          className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                          title="Herunterladen"
                        >
                          <FiDownload />
                        </button>
                        <button
                          onClick={() => {
                            setEditingDocument(doc.id);
                            setEditFilename(doc.filename);
                          }}
                          className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                          title="Umbenennen"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id, doc.filename)}
                          className="p-1 rounded text-red-600 hover:text-red-800"
                          title="Löschen"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistiken */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center">
            <FiFileText className="text-blue-500 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gesamt</p>
              <p className="text-2xl font-bold">{documents.length}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center">
            <FiEye className="text-green-500 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Aktiv</p>
              <p className="text-2xl font-bold">
                {documents.filter((d) => d.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center">
            <FiRefreshCw className="text-yellow-500 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Verarbeitung</p>
              <p className="text-2xl font-bold">
                {documents.filter((d) => d.status === 'processing').length}
              </p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center">
            <FiTrash2 className="text-red-500 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Fehler</p>
              <p className="text-2xl font-bold">
                {documents.filter((d) => d.status === 'failed').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dokument-Vorschau Modal */}
      {viewingDocument && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={closeDocumentView}
        >
          <div
            className={`relative w-11/12 h-5/6 max-w-6xl rounded-lg shadow-2xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className={`flex items-center justify-between p-4 border-b ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center">
                <FiEye className="text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold">
                  {decodeFilename(viewingDocument.filename)}
                </h3>
                {viewingDocument.chunking_strategy === 'detailreich' && (
                  <span className="ml-3 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-1 rounded">
                    Detailreich
                  </span>
                )}
              </div>
              <button
                onClick={closeDocumentView}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
                title="Schließen"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="h-full overflow-hidden">
              {isLoadingDocument ? (
                <div className="flex items-center justify-center h-full">
                  <FiRefreshCw className="animate-spin text-4xl text-blue-500" />
                  <span className="ml-3 text-lg">Dokument wird geladen...</span>
                </div>
              ) : documentError ? (
                <div className="flex flex-col items-center justify-center h-full text-red-500">
                  <span className="text-4xl mb-4">❌</span>
                  <span className="text-lg mb-2">Fehler beim Laden</span>
                  <span className="text-sm text-gray-500">{documentError}</span>
                </div>
              ) : documentUrl ? (
                <iframe
                  src={documentUrl}
                  className="w-full h-full border-0"
                  title={`Vorschau: ${viewingDocument.filename}`}
                  style={{ height: 'calc(100% - 60px)' }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
